import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    MenuItem,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
    Breadcrumbs,
    Link,
    Typography,
    Autocomplete
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import notificationService from '../../../shared/services/notificationService';
import { getProductNames } from '../../../shared/services/productService';
import {
    NotificationType,
    NotificationPriority,
    TargetAudience,
    SendNotificationPayload
} from '../../../shared/types/notification';

export const SendNotification: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });

    const [formData, setFormData] = useState<SendNotificationPayload>({
        title: '',
        message: '',
        type: 'home',
        targetAudience: 'customer',
        targetUserIds: []
    });

    const [products, setProducts] = useState<Array<{ productId: number; name: string }>>([]);
    const [selectedProduct, setSelectedProduct] = useState<{ productId: number; name: string } | null>(null);
    const [productsLoading, setProductsLoading] = useState(false);

    // Fetch product list when type = productDetails
    useEffect(() => {
        if (formData.type === 'productDetails') {
            setProductsLoading(true);
            getProductNames()
                .then(res => {
                    if (res.success && res.data) setProducts(res.data);
                })
                .catch(() => setProducts([]))
                .finally(() => setProductsLoading(false));
        } else {
            setSelectedProduct(null);
        }
    }, [formData.type]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof SendNotificationPayload] as any),
                    [child]: value
                }
            }));
        } else if (name === 'targetAudience') {
            // Reset type when target audience changes
            const defaultType = (value === 'delivery' ? 'deliveryHome' : 'home') as NotificationType;
            setFormData(prev => ({ ...prev, targetAudience: value as TargetAudience, type: defaultType }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const customerTypeOptions = [
        { value: 'home', label: 'Home' },
        { value: 'productList', label: 'Product List' },
        { value: 'productDetails', label: 'Product Details' },
        { value: 'cart', label: 'Cart' },
    ];

    const deliveryTypeOptions = [
        { value: 'deliveryHome', label: 'Home' },
        { value: 'deliveryOrderList', label: 'Order List' },
    ];

    const typeOptions = formData.targetAudience === 'delivery' ? deliveryTypeOptions : customerTypeOptions;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title || !formData.message) {
            setSnackbar({ open: true, message: 'Title and Message are required', severity: 'error' });
            return;
        }
        if (formData.type === 'productDetails' && !selectedProduct) {
            setSnackbar({ open: true, message: 'Please select a product', severity: 'error' });
            return;
        }

        setIsLoading(true);
        try {
            const payload: SendNotificationPayload = {
                ...formData,
                ...(formData.type === 'productDetails' && selectedProduct
                    ? { reference_id: String(selectedProduct.productId) }
                    : {}),
            };
            const response = await notificationService.sendNotification(payload);
            if (response.success) {
                setSnackbar({ open: true, message: 'Notification sent successfully', severity: 'success' });
                setTimeout(() => navigate('/notifications'), 1500);
            }
        } catch (error: any) {
            console.error('Error sending notification:', error);
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to send notification', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-gray-50">
            <div className="p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <Breadcrumbs aria-label="breadcrumb" className="mb-4">
                    <Link underline="hover" color="inherit" onClick={() => navigate('/notifications')} className="cursor-pointer">
                        Notifications
                    </Link>
                    <Typography color="text.primary">Send New</Typography>
                </Breadcrumbs>
                <h2 className="text-3xl font-extrabold text-gray-900">Send Notification</h2>
            </div>

            <div className="p-6 sm:p-10 flex-1 overflow-y-auto">
                <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 max-w-4xl mx-auto">
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                placeholder="Enter notification title"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                multiline
                                rows={4}
                                placeholder="Write your notification message here..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Target Audience"
                                name="targetAudience"
                                value={formData.targetAudience}
                                onChange={handleChange}
                            >
                                <MenuItem value="customer">Customer</MenuItem>
                                <MenuItem value="delivery">Delivery</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                            >
                                {typeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Product selector — shown only when type = productDetails */}
                        {formData.type === 'productDetails' && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={products}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedProduct}
                                    onChange={(_e, newValue) => setSelectedProduct(newValue)}
                                    loading={productsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Product"
                                            required
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {productsLoading ? <CircularProgress size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} className="flex justify-end gap-4 mt-6">
                            <AppButton
                                variant="secondary"
                                onClick={() => navigate('/notifications')}
                                disabled={isLoading}
                            >
                                Cancel
                            </AppButton>
                            <AppButton
                                type="submit"
                                variant="primary"
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isLoading ? 'Sending...' : 'Send Notification'}
                            </AppButton>
                        </Grid>
                    </Grid>
                </form>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};
